/**
 * Logger utility that only logs in development environment
 * This helps prevent console logs from appearing in production builds
 */

import { isDevelopment } from './environment'

/* eslint-disable no-console */

interface Logger {
    log: (...args: unknown[]) => void
    warn: (...args: unknown[]) => void
    error: (...args: unknown[]) => void
    info: (...args: unknown[]) => void
    debug: (...args: unknown[]) => void
    group: (...args: unknown[]) => void
    groupEnd: () => void
    table: (data: unknown) => void
    time: (label?: string) => void
    timeEnd: (label?: string) => void
}

/**
 * Development-only logger
 * Only outputs logs when in development environment
 */
export const logger: Logger = {
    log: (...args: unknown[]) => {
        if (isDevelopment) {
            console.log(...args)
        }
    },
    warn: (...args: unknown[]) => {
        if (isDevelopment) {
            console.warn(...args)
        }
    },
    error: (...args: unknown[]) => {
        if (isDevelopment) {
            console.error(...args)
        }
    },
    info: (...args: unknown[]) => {
        if (isDevelopment) {
            console.info(...args)
        }
    },
    debug: (...args: unknown[]) => {
        if (isDevelopment) {
            console.debug(...args)
        }
    },
    group: (...args: unknown[]) => {
        if (isDevelopment) {
            console.group(...args)
        }
    },
    groupEnd: () => {
        if (isDevelopment) {
            console.groupEnd()
        }
    },
    table: (data: unknown) => {
        if (isDevelopment) {
            console.table(data)
        }
    },
    time: (label?: string) => {
        if (isDevelopment) {
            console.time(label)
        }
    },
    timeEnd: (label?: string) => {
        if (isDevelopment) {
            console.timeEnd(label)
        }
    },
}

/**
 * Always log errors regardless of environment
 * Use this for critical errors that should always be logged
 */
export const criticalLogger = {
    error: (...args: unknown[]) => {
        if (isDevelopment) {
            console.error(...args)
        }
        // In production, these are handled by the global error handler
    },
    warn: (...args: unknown[]) => {
        if (isDevelopment) {
            console.warn(...args)
        }
        // In production, these are handled by the global error handler
    },
}

/**
 * Production-safe critical logger that only logs in development
 * Use this if you want critical logs to also respect environment settings
 */
export const productionSafeCriticalLogger = {
    error: (...args: unknown[]) => {
        if (isDevelopment) {
            console.error(...args)
        }
        // In production, these are completely silent - no logging at all
    },
    warn: (...args: unknown[]) => {
        if (isDevelopment) {
            console.warn(...args)
        }
        // In production, these are completely silent - no logging at all
    },
}

export default logger
